import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, fleet, type CommandLogRow } from "@/api/client";
import { PageHead } from "@/components/PageHead";
import { DataTable, type DataCol } from "@/components/DataTable";
import { Select, SearchInput } from "@/components/inputs";
import { ActionRow } from "@/components/ActionRow";
import { Icon } from "@/components/icons";
import { DevicePicker } from "@/modules/asset_data/picker";
import { AssetDetail } from "@/modules/fleet/AssetDetail";
import { CollapsibleSection } from "@/components/Section";
import { NewRun } from "@/modules/fleet/NewRun";
import { RunCard } from "@/modules/fleet/RunCard";
import { ACT_LABEL, LEVEL_LABEL, STATE_MEANS, StateChip, actionClass,
         commandLabel, commandSpec, reasonText } from "@/modules/fleet/catalogue";
import { CAP, useSession } from "@/auth/session";
import { POLL, ago, until } from "@/lib/format";

/** Fleet — one page: choose a command, choose who it applies to, run it.
 *
 *  WHY ONE PAGE. It was two subpages, Commands and Asset Control, and
 *  Dattatray rejected that: *"Asset control subpage doe snot make any
 *  sesnse!"* He was right, and the reason is the one that killed the split —
 *  he had just asked for the target selector to have **two parts, a single
 *  asset with the same filters and a bulk apply**. Once the command page can
 *  target one asset, a separate page for one asset is a second way to do one
 *  thing. D-101's rule, applied the other direction: one decision, one place.
 *
 *  THE ORDER IS THE FIX TO THE OLD COMMAND BOX, which he also called out as
 *  "entirely incoorect". It used to show an "Immobilise as" dropdown
 *  unconditionally — present and meaningless when you intended to mobilise —
 *  next to four controls with no primary among them. Now the COMMAND is
 *  chosen first, and everything downstream follows from its catalogue entry:
 *  its options appear only if it has any, and there is exactly ONE action
 *  button, carrying that command's label, colour and icon.
 *
 *  POLL CADENCE: the per-asset view and the batch rollups are operational
 *  state (POLL.STATE); the fleet-wide log is a list a human reads
 *  (POLL.LIST). Budgeting them differently is the UI performance law, not a
 *  preference — two 2 s polls on one page is how the console earned it.
 */

//: the two commands the quick panel offers, read from the catalogue so a
//: rename there is a compile error here rather than a dead button
const IMMOB = commandSpec("immobilize")!;
const MOBIL = commandSpec("mobilize")!;

/** States a command can no longer leave. Mirrors `COMMAND_TERMINAL` on the
 *  server; a row in one of these has nothing left to cancel and no deadline
 *  worth showing. */
const TERMINAL = new Set([
  "applied", "nacked", "superseded", "expired_undelivered",
  "expired_unapplied", "cancelled",
]);

export function FleetPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const qc = useQueryClient();
  const mayCommand = can(CAP.IMMOBILIZE);

  //: WHICH COMMAND. Everything below reads from this entry rather than
  //: deciding for itself, so a new catalogue row needs no edit here.

  const [assetId, setAssetId] = useState("");
  //: the picker's own selection. A hold belongs to the ASSET (D-35: it
  //: outlives the box), so this only drives the picker and warns when a
  //: search named a stream with no asset behind it.
  const [stream, setStream] = useState("");
  const [action, setAction] = useState<number>(IMMOB.options[0]?.action ?? 2);
  const [note, setNote] = useState("");
  //: "" means "let the server pick the default for this action" — a sentinel
  //: rather than a number, so the UI never has to restate 24h/15min as its
  //: own constant and drift from the server's.
  const [lifeMin, setLifeMin] = useState("");

  // `?asset=` is how the fleet map hands an asset over, the same shape the
  // Window and History pages use for `?imei=`.
  const deep = params.get("asset");
  useEffect(() => {
    if (!deep) return;
    setAssetId(deep);
    setParams((prev) => { const p = new URLSearchParams(prev); p.delete("asset"); return p; },
              { replace: true });
  }, [deep]);

  const q = useQuery({
    queryKey: ["fleet", "asset", assetId],
    queryFn: () => fleet.asset(assetId),
    enabled: assetId.trim().length > 0,
    refetchInterval: POLL.STATE,
  });
  const batches = useQuery({
    queryKey: ["fleet", "batches"],
    queryFn: () => fleet.batches(),
    refetchInterval: POLL.STATE,
  });
  const log = useQuery({
    queryKey: ["fleet", "log"],
    queryFn: () => fleet.commands(),
    refetchInterval: POLL.LIST,
  });

  // STEP-UP. The server answers 428 STEP_UP_REQUIRED, which is a PROMPT and
  // not a failure — the client can fix it by asking for a code, which is
  // exactly why the status is 428 and not 403. Shown inline rather than as a
  // modal: an operator who has just chosen an asset should not lose it.
  const [needsStepUp, setNeedsStepUp] = useState(false);
  const [code, setCode] = useState("");
  const stepUp = useMutation({
    mutationFn: () => fleet.stepUp(code.trim()),
    onSuccess: () => { setNeedsStepUp(false); setCode(""); },
  });

  const act = useMutation({
    mutationFn: (what: "immobilize" | "mobilize") => {
      const life = lifeMin ? Number(lifeMin) : undefined;
      return what === "immobilize"
        ? fleet.immobilize(assetId, {
            action, note: note || undefined, lifetime_minutes: life })
        : fleet.mobilize(assetId, undefined, life);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fleet", "asset", assetId] }),
    onError: (e: unknown) => {
      // ON THE STATUS, NOT THE MESSAGE. This matched the string
      // "STEP_UP_REQUIRED" inside the error text — and the server sent its
      // body as {"code": ...} while the client reads `detail.error_code`, so
      // the message was the fallback `HTTP_428` and the match NEVER fired.
      // Dattatray got a raw "HTTP_428" where the page should have asked him
      // to confirm. 428 is the status the server chose precisely so a client
      // could branch on it, so branch on it.
      if ((e as ApiError)?.status === 428) setNeedsStepUp(true);
    },
  });

  const v = q.data;
  //: FOTA's split: archived runs leave the active list but keep their state.
  const allRuns = batches.data?.batches ?? [];
  const liveRuns = allRuns.filter((b) => !b.archived_at);
  const archivedRuns = allRuns.filter((b) => b.archived_at);
  //: THIS LEVEL'S live command, if any. Read off the view the page already
  //: polls rather than a second request — the active list is exactly the
  //: commands that can still change.
  const inFlight = (v?.commands_active ?? v?.commands ?? [])
    .find((c) => c.state === "pending" || c.state === "sent"
                 || c.state === "received");

  return (
    <div className="mpage on">
      <PageHead eyebrow="Fleet · Commands" title="Command the fleet"
        desc={<>Nothing is in force until the device says <b>applied</b>.</>} />

      {/* COMMAND ONE ASSET — the quick path, kept because an operator
          stopping a single vehicle should not have to create a run for it.
          It has NO command picker of its own: two buttons say what they do.
          The picker lives in New run, and having both was the duplication
          Dattatray called out when two controls asked the same question. */}
      <div className="section-label flush">Command one asset</div>
      <div className="panel panel-pad">
        <DevicePicker imei={stream} onPick={setStream}
          onPickAsset={(a) => setAssetId(a ?? "")} />
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end",
                      marginTop: 12, flexWrap: "wrap" }}>
          <div>
            <label className="simlab">Asset (VIN / BIN)</label>
            <SearchInput mono style={{ width: 280 }}
              placeholder="DRILL-ASSET-001" value={assetId}
              onChange={(e) => setAssetId(e.target.value)} />
          </div>
          <div>
            <label className="simlab">Immobilise as</label>
            <Select style={{ width: 210 }} value={action}
              onChange={(e) => setAction(Number(e.target.value))}>
              {IMMOB.options.map((o) => (
                <option key={o.action} value={o.action}>{o.label}</option>
              ))}
            </Select>
          </div>
          <div>
            <label className="simlab">Retry delivery for</label>
            <Select style={{ width: 160 }} value={lifeMin}
              onChange={(e) => setLifeMin(e.target.value)}>
              <option value="">Default</option>
              <option value="5">5 minutes</option>
              <option value="60">1 hour</option>
              <option value="1440">24 hours</option>
              <option value="10080">7 days</option>
            </Select>
          </div>
          <div>
            <label className="simlab">Note (optional)</label>
            <SearchInput style={{ width: 200 }} placeholder="why"
              value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
        {stream && !assetId && (
          <div style={{ fontSize: 11.5, color: "var(--warn)", marginTop: 8 }}>
            That is a data stream, not an asset. Search by Asset.
          </div>
        )}
        {inFlight && (
          <div style={{ marginTop: 10, padding: "10px 12px",
                borderRadius: 9, border: "1px solid var(--line)",
                borderLeft: "3px solid var(--warn)" }}>
            <b style={{ color: "var(--warn)" }}>Already in flight</b>
            <div style={{ fontSize: 12.5, marginTop: 4 }}>
              {ACT_LABEL[inFlight.action] ?? inFlight.action}, sent{" "}
              {ago(inFlight.created_at)} ago. A new command supersedes it.
            </div>
          </div>
        )}
        <ActionRow>
          {!mayCommand && (
            <span style={{ fontSize: 11.5, color: "var(--t-mute)",
                           marginRight: "auto" }}>
              Needs the immobilise permission.
            </span>
          )}
          {mayCommand && act.isError && !needsStepUp && (
            <span style={{ fontSize: 11.5, color: "var(--bad)",
                           marginRight: "auto" }}>
              {String((act.error as Error)?.message ?? "command failed")}
            </span>
          )}
          <button className={actionClass(MOBIL)}
            disabled={!assetId || !mayCommand || act.isPending}
            onClick={() => act.mutate("mobilize")}>
            <Icon name={MOBIL.icon} /> {MOBIL.label}
          </button>
          <button className={actionClass(IMMOB)}
            disabled={!assetId || !mayCommand || act.isPending}
            onClick={() => act.mutate("immobilize")}>
            <Icon name={IMMOB.icon} /> {IMMOB.label}
          </button>
        </ActionRow>
      </div>

      {needsStepUp && (
        <div className="panel panel-pad" style={{ marginTop: 14,
              borderLeft: "3px solid var(--warn)" }}>
          <b style={{ color: "var(--warn)" }}>Confirm it is you</b>
          <div style={{ fontSize: 12.5, marginTop: 4 }}>
            A code from your authenticator app, or a backup code. Valid five
            minutes. <b>Releasing never needs this.</b>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end",
                        marginTop: 10 }}>
            <div>
              <label className="simlab">6-digit code or backup code</label>
              <SearchInput mono style={{ width: 180 }} value={code}
                autoComplete="one-time-code"
                onChange={(e) => setCode(e.target.value)} />
            </div>
            <button className="btn teal" disabled={!code.trim() || stepUp.isPending}
              onClick={() => stepUp.mutate()}>Confirm</button>
            {stepUp.isError && (
              <span style={{ fontSize: 11.5, color: "var(--bad)" }}>
                That code was not accepted.
              </span>
            )}
          </div>
        </div>
      )}

      {/* 3 — THE ASSET ITSELF, once one is chosen. */}
      {!assetId && (
        <div className="panel panel-pad cap" style={{ marginTop: 14 }}>
          Choose an asset, or pick one on the{" "}
          <Link to="/asset-data/overview">map</Link>.
        </div>
      )}
      {assetId && q.isError && (
        <div className="panel panel-pad" style={{ marginTop: 14,
              borderLeft: "3px solid var(--bad)" }}>
          {/* SAY WHICH FAILURE IT WAS. This read "No such asset, or it is not
              yours" for EVERY error, including a 500 — so a crash in
              `fota_history` presented to a super admin as a permissions
              problem on an asset he owns, and he went looking at scoping.
              A wrong diagnosis in an error message costs more than no
              message: it sends the reader somewhere else entirely. */}
          {/NOT_FOUND|NO_SUCH_ASSET|404/.test(
              String((q.error as Error)?.message ?? ""))
            ? <>No such asset, or it is not yours.</>
            : <>
                <b>That asset could not be loaded.</b>{" "}
                <span className="mono" style={{ fontSize: 11.5 }}>
                  {String((q.error as Error)?.message ?? "unknown error")}
                </span>
                <div style={{ fontSize: 11.5, color: "var(--t-mute)", marginTop: 6 }}>
                  This is a fault on our side, not a permissions problem — the
                  asset may well exist and be yours.
                </div>
              </>}
        </div>
      )}
      {v && <AssetDetail v={v} />}

      {/* NEW RUN, then ACTIVE, then ARCHIVED — FOTA's page order, because
          the question an operator arrives with is "start one", and the
          question they come back with is "how is it going". */}
      <div className="section-label">New run</div>
      <NewRun onCreated={() => qc.invalidateQueries({ queryKey: ["fleet"] })} />

      <div style={{ marginTop: 18 }}>
        <CollapsibleSection title="Active runs" count={liveRuns.length} defaultOpen>
          {liveRuns.length === 0
            ? <div className="panel panel-pad cap">No runs yet.</div>
            : liveRuns.map((b) => <RunCard key={b.batch_id} b={b} />)}
        </CollapsibleSection>
        {archivedRuns.length > 0 && (
          <CollapsibleSection title="Archived runs" count={archivedRuns.length}>
            {archivedRuns.map((b) => <RunCard key={b.batch_id} b={b} />)}
          </CollapsibleSection>
        )}
      </div>

      <div className="section-label">All commands</div>
      <LogTable rows={log.data?.commands ?? []} loading={log.isLoading}
        hasMore={log.data?.has_more ?? false}
        onPick={(a) => setAssetId(a)}
        onCancelled={() => {
          // the asset's own view carries the holds, and cancelling an
          // immobilise releases one — refetch both or the page shows a
          // restraint that no longer exists
          qc.invalidateQueries({ queryKey: ["fleet"] });
        }} />
    </div>
  );
}

/** "Immobilise · Freewheel (throttle cut)" — one spelling, used by the cell
 *  and by its own tooltip, so a truncated cell and its hover cannot disagree. */
function cmdLabel(c: CommandLogRow): string {
  return commandLabel(c.action);
}

/** Cancel, where cancelling is meaningful.
 *
 *  OFFERED ONLY WHILE ORBIT STILL OWNS THE OUTCOME. A terminal command has
 *  nothing to call off, and one the device has already ACKed is ARMED on the
 *  vehicle — the server refuses that with `COMMAND_ALREADY_ARMED` because
 *  only a MOBILISE un-arms a device, and showing a button that always fails
 *  would be worse than showing none. `received` is the state that says so.
 */
function CancelCell({ row, onCancelled }: {
  row: CommandLogRow; onCancelled: () => void;
}) {
  const { can } = useSession();
  const cancel = useMutation({
    mutationFn: () => fleet.cancelCommand(row.cmd_id),
    onSuccess: onCancelled,
  });
  if (TERMINAL.has(row.state) || row.state === "received") {
    return <span className="muted">—</span>;
  }
  if (!can(CAP.IMMOBILIZE)) return <span className="muted">—</span>;
  return (
    <>
      <button type="button" className="btn ghost sm"
        disabled={cancel.isPending} onClick={() => cancel.mutate()}>
        Cancel
      </button>
      {cancel.isError && (
        <div style={{ fontSize: 10.5, color: "var(--bad)" }}
          title={String((cancel.error as Error)?.message ?? "")}>
          refused
        </div>
      )}
    </>
  );
}

function LogTable({ rows, loading, hasMore, onPick, onCancelled }: {
  rows: CommandLogRow[]; loading: boolean; hasMore: boolean;
  onPick: (assetId: string) => void;
  onCancelled: () => void;
}) {
  const cols = useMemo<DataCol<CommandLogRow>[]>(() => [
    // THE WIDTH BUDGET IS REAL. `.mpage` is max-width 1240 with up to 28px of
    // padding each side, and DataTable uses `table-layout: fixed`, so columns
    // do not negotiate — they ellipsise and the table scrolls sideways. Ten
    // columns once totalled 1445px, which is why Dattatray saw "Immobilise ·
    // Freewheel (throttle c…", "Aphelion (platfo…", a clipped header and a
    // half-hidden Cancel button.
    //
    // The fix is FEWER columns, not narrower ones: STATE and MEANS are one
    // cell now (the chip, then what it means under it), which buys back 240px
    // and reads better besides — the meaning belongs to the state rather than
    // beside it. A render test holds the total under budget.
    { key: "when", label: "When", width: 70, render: (c) => ago(c.created_at) },
    { key: "asset", label: "Asset", width: 140, mono: true,
      title: (c) => c.asset_id ?? c.connection_id,
      render: (c) => c.asset_id
        ? <button type="button" onClick={() => onPick(c.asset_id as string)}
            style={{ font: "inherit", background: "none", border: 0, padding: 0,
                     color: "var(--link, var(--ok))", cursor: "pointer",
                     textDecoration: "underline" }}>{c.asset_id}</button>
        // NO ASSET MEANS THE BOX WAS COMMANDED BEFORE ITS ASSET WAS KNOWN.
        // Showing the IMEI rather than a blank keeps the row useful.
        : <span title="commanded by connection — no asset recorded">
            {c.connection_id}</span> },
    { key: "cmd", label: "Command", width: 220,
      title: (c) => cmdLabel(c), render: (c) => cmdLabel(c) },
    { key: "level", label: "Level", width: 125,
      title: (c) => c.level != null ? (LEVEL_LABEL[c.level] ?? `level ${c.level}`) : "",
      render: (c) => c.level != null ? (LEVEL_LABEL[c.level] ?? `level ${c.level}`) : "—" },
    { key: "state", label: "State", width: 230,
      title: (c) => STATE_MEANS[c.state] ?? c.state,
      render: (c) => (
        <>
          <StateChip state={c.state} />
          <div style={{ fontSize: 11, color: "var(--t-mute)", marginTop: 2,
                        whiteSpace: "normal", lineHeight: 1.25 }}>
            {STATE_MEANS[c.state] ?? "—"}
          </div>
        </>
      ) },
    { key: "reason", label: "Reason", width: 110,
      title: (c) => reasonText(c.reason_code),
      render: (c) => reasonText(c.reason_code) },
    { key: "until", label: "Retries until", width: 95,
      title: () => "when Orbit stops retrying delivery — the hold itself does not lift",
      render: (c) => TERMINAL.has(c.state)
        ? <span className="muted">—</span>
        : <span style={{ fontSize: 12 }}>
            {c.expires_at ? `in ${until(c.expires_at)}` : "—"}</span> },
    { key: "batch", label: "Batch", width: 75, mono: true,
      title: (c) => c.batch_id ?? "single command",
      render: (c) => c.batch_id
        ? c.batch_id.slice(0, 8)
        : <span className="muted">—</span> },
    { key: "act", label: "", width: 80,
      render: (c) => <CancelCell row={c} onCancelled={onCancelled} /> },
  ], [onPick, onCancelled]);

  if (!loading && rows.length === 0) {
    return <div className="panel panel-pad cap">No commands recorded yet.</div>;
  }
  return (
    <>
      <DataTable rows={rows} cols={cols} rowKey={(c) => c.cmd_id} pageSize={25} />
      {hasMore && (
        <div style={{ fontSize: 11.5, color: "var(--t-mute)", margin: "8px 2px 0" }}>
          Most recent {rows.length}.
        </div>
      )}
    </>
  );
}
